import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@repo/ui'
import { createFileRoute } from '@tanstack/react-router'
import {
  AlertTriangle,
  BarChart3,
  Calendar,
  CheckCircle2,
  Clock,
  DollarSign,
  Download,
  FileText,
  Gauge,
  Route as RouteIcon,
  Shield,
  Target,
  Timer,
  TrendingDown,
  TrendingUp,
  Truck,
  Users,
  Zap,
} from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'

import { VehicleStatusBadge } from '@/components/shared/vehicle-status-badge'
import { useDocumentTitle } from '@/hooks/use-document-title'
import { useDrivers, useTasks, useVehicles } from '@/hooks/api-hooks'
import { formatCurrency, formatNumber } from '@/lib/format'
import { localizedPageTitle } from '@/lib/page-title'

export const Route = createFileRoute('/dashboard/reports/')({
  head: () => ({ meta: [{ title: localizedPageTitle('reports') }] }),
  component: ReportsPage,
})

const tooltipStyle = {
  backgroundColor: '#ffffff',
  border: '1px solid #e2e8f0',
  borderRadius: '4px',
  padding: '12px',
  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
  fontSize: '12px',
}

const colors = {
  primary: '#1e40af',
  secondary: '#475569',
  success: '#15803d',
  warning: '#b45309',
  danger: '#b91c1c',
  info: '#0369a1',
  muted: '#64748b',
  chart1: '#1e40af',
  chart2: '#0f766e',
  chart3: '#6d28d9',
  chart4: '#0369a1',
  chart5: '#b45309',
}

export default function ReportsPage() {
  useDocumentTitle('reports')
  const { t } = useTranslation('reports')
  const { data: vehicles, isLoading: vehiclesLoading } = useVehicles()
  const { data: drivers, isLoading: driversLoading } = useDrivers()
  const { data: tasks, isLoading: tasksLoading } = useTasks()
  const [timeRange, setTimeRange] = useState<string>('30days')
  const [activeTab, setActiveTab] = useState('overview')

  const isLoading = vehiclesLoading || driversLoading || tasksLoading

  const metrics = {
    totalVehicles: vehicles?.length || 0,
    activeVehicles: vehicles?.filter((v) => v.status === 'active').length || 0,
    totalDrivers: drivers?.length || 0,
    activeDrivers: drivers?.filter((d) => d.status === 'driving').length || 0,
    totalRoutes: 0,
    completedRoutes: 0,
    totalTasks: tasks?.length || 0,
    completedTasks: tasks?.filter((t) => t.status === 'completed').length || 0,
    averageFuel: vehicles
      ? vehicles.reduce((sum, v) => sum + v.fuel, 0) / vehicles.length
      : 0,
    totalMileage: vehicles
      ? vehicles.reduce((sum, v) => sum + v.mileage, 0)
      : 0,
    // Financial metrics
    totalRevenue: 128450.0,
    totalCosts: 45230.5,
    fuelCosts: 28450.75,
    maintenanceCosts: 12340.0,
    laborCosts: 4439.75,
    profitMargin: 64.8,
    // Operational metrics
    avgDeliveryTime: 4.2,
    onTimeDeliveryRate: 94.5,
    avgLoadCapacity: 78.3,
    incidentCount: 3,
    complianceRate: 98.7,
    // Efficiency metrics
    fuelEfficiency: 8.2,
    idleTimePercent: 12.4,
    utilizationRate: 82.5,
  }

  const monthlyFinancialData = [
    { month: 'Jan', revenue: 98500, costs: 38200, profit: 60300 },
    { month: 'Feb', revenue: 102300, costs: 41500, profit: 60800 },
    { month: 'Mar', revenue: 115800, costs: 43200, profit: 72600 },
    { month: 'Apr', revenue: 108900, costs: 40100, profit: 68800 },
    { month: 'May', revenue: 121500, costs: 44800, profit: 76700 },
    { month: 'Jun', revenue: 128450, costs: 45230, profit: 83220 },
  ]

  const weeklyOperationalData = [
    { week: 'W1', deliveries: 145, onTime: 138, delayed: 7, distance: 12450 },
    { week: 'W2', deliveries: 162, onTime: 155, delayed: 7, distance: 14200 },
    { week: 'W3', deliveries: 158, onTime: 149, delayed: 9, distance: 13800 },
    { week: 'W4', deliveries: 171, onTime: 162, delayed: 9, distance: 15100 },
  ]

  const fuelByVehicleType = [
    { type: 'Heavy Truck', consumption: 32.5, cost: 12450, vehicles: 4 },
    { type: 'Medium Truck', consumption: 24.8, cost: 8920, vehicles: 6 },
    { type: 'Light Van', consumption: 12.2, cost: 4280, vehicles: 8 },
    { type: 'Refrigerated', consumption: 38.2, cost: 2800, vehicles: 2 },
  ]

  const driverMetrics =
    drivers?.map((driver, index) => ({
      id: driver.id,
      name: driver.name,
      deliveries: 45 + index * 8,
      onTimeRate: 92 + (index % 3) * 2.5,
      fuelEfficiency: 7.8 + (index % 4) * 0.4,
      safetyScore: 88 + (index % 5) * 2.4,
      hoursWorked: driver.hoursWorked,
      incidents: index % 3 === 0 ? 1 : 0,
      revenue: 8500 + index * 1200,
    })) || []

  const routeEfficiencyData = [
    {
      route: 'Route A',
      planned: 180,
      actual: 175,
      efficiency: 97.2,
      deliveries: 24,
    },
    {
      route: 'Route B',
      planned: 220,
      actual: 235,
      efficiency: 93.6,
      deliveries: 31,
    },
    {
      route: 'Route C',
      planned: 150,
      actual: 148,
      efficiency: 98.7,
      deliveries: 18,
    },
    {
      route: 'Route D',
      planned: 195,
      actual: 202,
      efficiency: 96.5,
      deliveries: 27,
    },
    {
      route: 'Route E',
      planned: 165,
      actual: 160,
      efficiency: 96.9,
      deliveries: 22,
    },
  ]

  const costBreakdownData = [
    { category: 'Fuel', value: 28450.75, percent: 62.9 },
    { category: 'Maintenance', value: 12340.0, percent: 27.3 },
    { category: 'Labor', value: 4439.75, percent: 9.8 },
  ]

  const dailyMetricsData = [
    { day: 'Mon', distance: 2450, fuel: 298, deliveries: 38, revenue: 4250 },
    { day: 'Tue', distance: 2680, fuel: 326, deliveries: 42, revenue: 4680 },
    { day: 'Wed', distance: 2520, fuel: 307, deliveries: 40, revenue: 4420 },
    { day: 'Thu', distance: 2890, fuel: 352, deliveries: 45, revenue: 5120 },
    { day: 'Fri', distance: 2750, fuel: 335, deliveries: 43, revenue: 4890 },
    { day: 'Sat', distance: 1980, fuel: 241, deliveries: 31, revenue: 3520 },
    { day: 'Sun', distance: 1450, fuel: 177, deliveries: 22, revenue: 2580 },
  ]

  const handleExport = (format: 'csv' | 'pdf') => {
    alert(`Report exported as ${format.toUpperCase()}`)
  }

  const handleGenerateReport = () => {
    alert('Custom report generated successfully!')
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">
            {t('title')}
          </h1>
          <p className="text-muted-foreground mt-0.5 text-sm">
            {t('subtitle')}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Select value={timeRange} onValueChange={setTimeRange}>
            <SelectTrigger className="h-9 w-36 text-sm">
              <Calendar className="text-muted-foreground mr-2 h-3.5 w-3.5" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7days">{t('last7Days')}</SelectItem>
              <SelectItem value="30days">{t('last30Days')}</SelectItem>
              <SelectItem value="90days">Last 90 Days</SelectItem>
              <SelectItem value="year">This Year</SelectItem>
            </SelectContent>
          </Select>
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleExport('csv')}
          >
            <Download className="mr-1.5 h-3.5 w-3.5" />
            CSV
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleExport('pdf')}
          >
            <FileText className="mr-1.5 h-3.5 w-3.5" />
            PDF
          </Button>
          <Button size="sm" onClick={handleGenerateReport}>
            <BarChart3 className="mr-1.5 h-3.5 w-3.5" />
            Generate Report
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <Card className="border-l-4 border-l-blue-600">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-muted-foreground text-xs font-medium tracking-wide uppercase">
                  Total Revenue
                </p>
                <p className="mt-1 text-2xl font-semibold">
                  {formatCurrency(metrics.totalRevenue)}
                </p>
                <div className="mt-1 flex items-center gap-1">
                  <TrendingUp className="h-3 w-3 text-green-600" />
                  <span className="text-xs font-medium text-green-600">
                    +12.5%
                  </span>
                  <span className="text-muted-foreground text-xs">
                    vs last period
                  </span>
                </div>
              </div>
              <div className="rounded-lg bg-blue-50 p-2.5 dark:bg-blue-950">
                <DollarSign className="h-5 w-5 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-emerald-600">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-muted-foreground text-xs font-medium tracking-wide uppercase">
                  Profit Margin
                </p>
                <p className="mt-1 text-2xl font-semibold">
                  {metrics.profitMargin}%
                </p>
                <div className="mt-1 flex items-center gap-1">
                  <TrendingUp className="h-3 w-3 text-green-600" />
                  <span className="text-xs font-medium text-green-600">
                    +3.2%
                  </span>
                  <span className="text-muted-foreground text-xs">
                    improvement
                  </span>
                </div>
              </div>
              <div className="rounded-lg bg-emerald-50 p-2.5 dark:bg-emerald-950">
                <TrendingUp className="h-5 w-5 text-emerald-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-amber-600">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-muted-foreground text-xs font-medium tracking-wide uppercase">
                  On-Time Delivery
                </p>
                <p className="mt-1 text-2xl font-semibold">
                  {metrics.onTimeDeliveryRate}%
                </p>
                <div className="mt-1 flex items-center gap-1">
                  <TrendingDown className="h-3 w-3 text-amber-600" />
                  <span className="text-xs font-medium text-amber-600">
                    -1.2%
                  </span>
                  <span className="text-muted-foreground text-xs">
                    vs target
                  </span>
                </div>
              </div>
              <div className="rounded-lg bg-amber-50 p-2.5 dark:bg-amber-950">
                <Clock className="h-5 w-5 text-amber-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-slate-600">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-muted-foreground text-xs font-medium tracking-wide uppercase">
                  Fleet Utilization
                </p>
                <p className="mt-1 text-2xl font-semibold">
                  {metrics.utilizationRate}%
                </p>
                <div className="mt-1 flex items-center gap-1">
                  <span className="text-muted-foreground text-xs">
                    {metrics.activeVehicles}/{metrics.totalVehicles} active
                  </span>
                </div>
              </div>
              <div className="rounded-lg bg-slate-100 p-2.5 dark:bg-slate-800">
                <Truck className="h-5 w-5 text-slate-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-muted/50 p-1">
          <TabsTrigger value="overview" className="text-xs">
            Overview
          </TabsTrigger>
          <TabsTrigger value="financial" className="text-xs">
            Financial
          </TabsTrigger>
          <TabsTrigger value="operations" className="text-xs">
            Operations
          </TabsTrigger>
          <TabsTrigger value="drivers" className="text-xs">
            Drivers
          </TabsTrigger>
          <TabsTrigger value="fleet" className="text-xs">
            Fleet
          </TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="mt-4 space-y-4">
          {/* Revenue vs Costs Chart */}
          <div className="grid gap-4 lg:grid-cols-3">
            <Card className="lg:col-span-2">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium">
                  Revenue & Cost Analysis
                </CardTitle>
                <CardDescription className="text-xs">
                  6-month financial performance trend
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={280}>
                  <ComposedChart data={monthlyFinancialData}>
                    <CartesianGrid
                      strokeDasharray="3 3"
                      stroke="#e2e8f0"
                      vertical={false}
                    />
                    <XAxis
                      dataKey="month"
                      tick={{ fontSize: 11 }}
                      stroke="#94a3b8"
                      axisLine={false}
                      tickLine={false}
                    />
                    <YAxis
                      tick={{ fontSize: 11 }}
                      stroke="#94a3b8"
                      axisLine={false}
                      tickLine={false}
                      tickFormatter={(v) => `€${v / 1000}k`}
                    />
                    <Tooltip
                      contentStyle={tooltipStyle}
                      formatter={(value) => formatCurrency(value as number)}
                    />
                    <Legend wrapperStyle={{ fontSize: '11px' }} />
                    <Bar
                      dataKey="revenue"
                      name="Revenue"
                      fill={colors.chart1}
                      radius={[2, 2, 0, 0]}
                    />
                    <Bar
                      dataKey="costs"
                      name="Costs"
                      fill={colors.chart2}
                      radius={[2, 2, 0, 0]}
                    />
                    <Line
                      type="monotone"
                      dataKey="profit"
                      name="Profit"
                      stroke={colors.success}
                      strokeWidth={2}
                      dot={{ r: 3 }}
                    />
                  </ComposedChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            {/* Cost Breakdown */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium">
                  Cost Breakdown
                </CardTitle>
                <CardDescription className="text-xs">
                  Total: {formatCurrency(metrics.totalCosts)}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {costBreakdownData.map((item, index) => (
                  <div key={item.category}>
                    <div className="mb-1.5 flex items-center justify-between">
                      <span className="text-sm font-medium">
                        {item.category}
                      </span>
                      <span className="text-muted-foreground text-sm">
                        {formatCurrency(item.value)}
                      </span>
                    </div>
                    <div className="bg-muted h-2 overflow-hidden rounded-full">
                      <div
                        className="h-full rounded-full transition-all"
                        style={{
                          width: `${item.percent}%`,
                          backgroundColor:
                            index === 0
                              ? colors.chart1
                              : index === 1
                                ? colors.chart2
                                : colors.chart4,
                        }}
                      />
                    </div>
                    <p className="text-muted-foreground mt-1 text-xs">
                      {item.percent}% of total
                    </p>
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>

          {/* Daily Metrics Area Chart */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">
                Daily Performance Metrics
              </CardTitle>
              <CardDescription className="text-xs">
                Distance, deliveries, and revenue tracking
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={240}>
                <AreaChart data={dailyMetricsData}>
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke="#e2e8f0"
                    vertical={false}
                  />
                  <XAxis
                    dataKey="day"
                    tick={{ fontSize: 11 }}
                    stroke="#94a3b8"
                    axisLine={false}
                    tickLine={false}
                  />
                  <YAxis
                    tick={{ fontSize: 11 }}
                    stroke="#94a3b8"
                    axisLine={false}
                    tickLine={false}
                  />
                  <Tooltip contentStyle={tooltipStyle} />
                  <Legend wrapperStyle={{ fontSize: '11px' }} />
                  <Area
                    type="monotone"
                    dataKey="distance"
                    name="Distance (km)"
                    stroke={colors.chart1}
                    fill={colors.chart1}
                    fillOpacity={0.1}
                    strokeWidth={2}
                  />
                  <Area
                    type="monotone"
                    dataKey="revenue"
                    name="Revenue (€)"
                    stroke={colors.success}
                    fill={colors.success}
                    fillOpacity={0.1}
                    strokeWidth={2}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* Summary Stats Grid */}
          <div className="grid grid-cols-2 gap-4 md:grid-cols-4 lg:grid-cols-6">
            <Card className="bg-muted/30">
              <CardContent className="p-3 text-center">
                <Gauge className="text-muted-foreground mx-auto mb-1 h-4 w-4" />
                <p className="text-lg font-semibold">
                  {metrics.fuelEfficiency} L/100km
                </p>
                <p className="text-muted-foreground text-xs">Fuel Efficiency</p>
              </CardContent>
            </Card>
            <Card className="bg-muted/30">
              <CardContent className="p-3 text-center">
                <Timer className="text-muted-foreground mx-auto mb-1 h-4 w-4" />
                <p className="text-lg font-semibold">
                  {metrics.avgDeliveryTime}h
                </p>
                <p className="text-muted-foreground text-xs">
                  Avg Delivery Time
                </p>
              </CardContent>
            </Card>
            <Card className="bg-muted/30">
              <CardContent className="p-3 text-center">
                <Target className="text-muted-foreground mx-auto mb-1 h-4 w-4" />
                <p className="text-lg font-semibold">
                  {metrics.avgLoadCapacity}%
                </p>
                <p className="text-muted-foreground text-xs">Load Capacity</p>
              </CardContent>
            </Card>
            <Card className="bg-muted/30">
              <CardContent className="p-3 text-center">
                <Zap className="text-muted-foreground mx-auto mb-1 h-4 w-4" />
                <p className="text-lg font-semibold">
                  {metrics.idleTimePercent}%
                </p>
                <p className="text-muted-foreground text-xs">Idle Time</p>
              </CardContent>
            </Card>
            <Card className="bg-muted/30">
              <CardContent className="p-3 text-center">
                <AlertTriangle className="text-muted-foreground mx-auto mb-1 h-4 w-4" />
                <p className="text-lg font-semibold">{metrics.incidentCount}</p>
                <p className="text-muted-foreground text-xs">Incidents</p>
              </CardContent>
            </Card>
            <Card className="bg-muted/30">
              <CardContent className="p-3 text-center">
                <Shield className="text-muted-foreground mx-auto mb-1 h-4 w-4" />
                <p className="text-lg font-semibold">
                  {metrics.complianceRate}%
                </p>
                <p className="text-muted-foreground text-xs">Compliance</p>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Financial Tab */}
        <TabsContent value="financial" className="mt-4 space-y-4">
          <div className="grid gap-4 lg:grid-cols-2">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium">
                  Monthly Revenue Trend
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={monthlyFinancialData}>
                    <CartesianGrid
                      strokeDasharray="3 3"
                      stroke="#e2e8f0"
                      vertical={false}
                    />
                    <XAxis
                      dataKey="month"
                      tick={{ fontSize: 11 }}
                      stroke="#94a3b8"
                      axisLine={false}
                      tickLine={false}
                    />
                    <YAxis
                      tick={{ fontSize: 11 }}
                      stroke="#94a3b8"
                      axisLine={false}
                      tickLine={false}
                      tickFormatter={(v) => `€${v / 1000}k`}
                    />
                    <Tooltip
                      contentStyle={tooltipStyle}
                      formatter={(value) => formatCurrency(value as number)}
                    />
                    <ReferenceLine
                      y={110000}
                      stroke="#94a3b8"
                      strokeDasharray="3 3"
                      label={{ value: 'Target', fontSize: 10, fill: '#94a3b8' }}
                    />
                    <Bar
                      dataKey="revenue"
                      fill={colors.chart1}
                      radius={[2, 2, 0, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium">
                  Profit Margin Trend
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={280}>
                  <LineChart data={monthlyFinancialData}>
                    <CartesianGrid
                      strokeDasharray="3 3"
                      stroke="#e2e8f0"
                      vertical={false}
                    />
                    <XAxis
                      dataKey="month"
                      tick={{ fontSize: 11 }}
                      stroke="#94a3b8"
                      axisLine={false}
                      tickLine={false}
                    />
                    <YAxis
                      tick={{ fontSize: 11 }}
                      stroke="#94a3b8"
                      axisLine={false}
                      tickLine={false}
                      tickFormatter={(v) => `€${v / 1000}k`}
                    />
                    <Tooltip
                      contentStyle={tooltipStyle}
                      formatter={(value) => formatCurrency(value as number)}
                    />
                    <Line
                      type="monotone"
                      dataKey="profit"
                      stroke={colors.success}
                      strokeWidth={2}
                      dot={{ r: 4, fill: colors.success }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </div>

          {/* Fuel Costs by Vehicle Type */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">
                Fuel Costs by Vehicle Type
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-xs">Vehicle Type</TableHead>
                    <TableHead className="text-right text-xs">
                      Vehicles
                    </TableHead>
                    <TableHead className="text-right text-xs">
                      Avg Consumption (L/100km)
                    </TableHead>
                    <TableHead className="text-right text-xs">
                      Total Cost
                    </TableHead>
                    <TableHead className="text-right text-xs">
                      Cost per Vehicle
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {fuelByVehicleType.map((item) => (
                    <TableRow key={item.type}>
                      <TableCell className="text-sm font-medium">
                        {item.type}
                      </TableCell>
                      <TableCell className="text-right text-sm">
                        {item.vehicles}
                      </TableCell>
                      <TableCell className="text-right text-sm">
                        {item.consumption}
                      </TableCell>
                      <TableCell className="text-right text-sm">
                        {formatCurrency(item.cost)}
                      </TableCell>
                      <TableCell className="text-right text-sm">
                        €{(item.cost / item.vehicles).toFixed(2)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Operations Tab */}
        <TabsContent value="operations" className="mt-4 space-y-4">
          <div className="grid gap-4 lg:grid-cols-2">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium">
                  Weekly Deliveries
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={weeklyOperationalData}>
                    <CartesianGrid
                      strokeDasharray="3 3"
                      stroke="#e2e8f0"
                      vertical={false}
                    />
                    <XAxis
                      dataKey="week"
                      tick={{ fontSize: 11 }}
                      stroke="#94a3b8"
                      axisLine={false}
                      tickLine={false}
                    />
                    <YAxis
                      tick={{ fontSize: 11 }}
                      stroke="#94a3b8"
                      axisLine={false}
                      tickLine={false}
                    />
                    <Tooltip contentStyle={tooltipStyle} />
                    <Legend wrapperStyle={{ fontSize: '11px' }} />
                    <Bar
                      dataKey="onTime"
                      name="On Time"
                      stackId="a"
                      fill={colors.success}
                      radius={[0, 0, 0, 0]}
                    />
                    <Bar
                      dataKey="delayed"
                      name="Delayed"
                      stackId="a"
                      fill={colors.warning}
                      radius={[2, 2, 0, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium">
                  Distance Covered (km)
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={280}>
                  <AreaChart data={weeklyOperationalData}>
                    <CartesianGrid
                      strokeDasharray="3 3"
                      stroke="#e2e8f0"
                      vertical={false}
                    />
                    <XAxis
                      dataKey="week"
                      tick={{ fontSize: 11 }}
                      stroke="#94a3b8"
                      axisLine={false}
                      tickLine={false}
                    />
                    <YAxis
                      tick={{ fontSize: 11 }}
                      stroke="#94a3b8"
                      axisLine={false}
                      tickLine={false}
                      tickFormatter={(v) => `${v / 1000}k`}
                    />
                    <Tooltip contentStyle={tooltipStyle} />
                    <Area
                      type="monotone"
                      dataKey="distance"
                      stroke={colors.chart4}
                      fill={colors.chart4}
                      fillOpacity={0.15}
                      strokeWidth={2}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </div>

          {/* Route Efficiency Table */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">
                Route Efficiency Analysis
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-xs">Route</TableHead>
                    <TableHead className="text-right text-xs">
                      Planned (km)
                    </TableHead>
                    <TableHead className="text-right text-xs">
                      Actual (km)
                    </TableHead>
                    <TableHead className="text-right text-xs">
                      Efficiency
                    </TableHead>
                    <TableHead className="text-right text-xs">
                      Deliveries
                    </TableHead>
                    <TableHead className="text-right text-xs">Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {routeEfficiencyData.map((route) => (
                    <TableRow key={route.route}>
                      <TableCell className="flex items-center gap-2 text-sm font-medium">
                        <RouteIcon className="text-muted-foreground h-3.5 w-3.5" />
                        {route.route}
                      </TableCell>
                      <TableCell className="text-right text-sm">
                        {route.planned}
                      </TableCell>
                      <TableCell className="text-right text-sm">
                        {route.actual}
                      </TableCell>
                      <TableCell className="text-right text-sm">
                        <span
                          className={
                            route.efficiency >= 96
                              ? 'text-green-600'
                              : 'text-amber-600'
                          }
                        >
                          {route.efficiency}%
                        </span>
                      </TableCell>
                      <TableCell className="text-right text-sm">
                        {route.deliveries}
                      </TableCell>
                      <TableCell className="text-right">
                        <Badge
                          variant={
                            route.efficiency >= 96 ? 'default' : 'secondary'
                          }
                          className="text-xs"
                        >
                          {route.efficiency >= 96 ? 'Optimal' : 'Review'}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Drivers Tab */}
        <TabsContent value="drivers" className="mt-4 space-y-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">
                Driver Performance Summary
              </CardTitle>
              <CardDescription className="text-xs">
                Comprehensive driver metrics and KPIs
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-xs">Driver</TableHead>
                    <TableHead className="text-right text-xs">
                      Deliveries
                    </TableHead>
                    <TableHead className="text-right text-xs">
                      On-Time %
                    </TableHead>
                    <TableHead className="text-right text-xs">
                      Fuel Eff.
                    </TableHead>
                    <TableHead className="text-right text-xs">Safety</TableHead>
                    <TableHead className="text-right text-xs">Hours</TableHead>
                    <TableHead className="text-right text-xs">
                      Revenue
                    </TableHead>
                    <TableHead className="text-right text-xs">Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {driverMetrics.map((driver) => (
                    <TableRow key={driver.id}>
                      <TableCell className="flex items-center gap-2 text-sm font-medium">
                        <Users className="text-muted-foreground h-3.5 w-3.5" />
                        {driver.name}
                      </TableCell>
                      <TableCell className="text-right text-sm">
                        {driver.deliveries}
                      </TableCell>
                      <TableCell className="text-right text-sm">
                        <span
                          className={
                            driver.onTimeRate >= 95
                              ? 'text-green-600'
                              : 'text-amber-600'
                          }
                        >
                          {driver.onTimeRate}%
                        </span>
                      </TableCell>
                      <TableCell className="text-right text-sm">
                        {driver.fuelEfficiency} L/100km
                      </TableCell>
                      <TableCell className="text-right text-sm">
                        <span
                          className={
                            driver.safetyScore >= 90
                              ? 'text-green-600'
                              : driver.safetyScore >= 80
                                ? 'text-amber-600'
                                : 'text-red-600'
                          }
                        >
                          {driver.safetyScore.toFixed(1)}
                        </span>
                      </TableCell>
                      <TableCell className="text-right text-sm">
                        {driver.hoursWorked}h
                      </TableCell>
                      <TableCell className="text-right text-sm">
                        {formatCurrency(driver.revenue)}
                      </TableCell>
                      <TableCell className="text-right">
                        {driver.incidents > 0 ? (
                          <Badge variant="destructive" className="text-xs">
                            <AlertTriangle className="mr-1 h-3 w-3" />
                            {driver.incidents}
                          </Badge>
                        ) : (
                          <Badge
                            variant="default"
                            className="bg-green-600 text-xs"
                          >
                            <CheckCircle2 className="mr-1 h-3 w-3" />
                            Clear
                          </Badge>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          {/* Driver Performance Chart */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">
                Driver Efficiency Comparison
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={driverMetrics} layout="vertical">
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke="#e2e8f0"
                    horizontal={false}
                  />
                  <XAxis
                    type="number"
                    tick={{ fontSize: 11 }}
                    stroke="#94a3b8"
                    axisLine={false}
                    tickLine={false}
                  />
                  <YAxis
                    dataKey="name"
                    type="category"
                    tick={{ fontSize: 11 }}
                    stroke="#94a3b8"
                    axisLine={false}
                    tickLine={false}
                    width={100}
                  />
                  <Tooltip contentStyle={tooltipStyle} />
                  <Legend wrapperStyle={{ fontSize: '11px' }} />
                  <Bar
                    dataKey="onTimeRate"
                    name="On-Time %"
                    fill={colors.chart1}
                    radius={[0, 2, 2, 0]}
                  />
                  <Bar
                    dataKey="safetyScore"
                    name="Safety Score"
                    fill={colors.chart2}
                    radius={[0, 2, 2, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Fleet Tab */}
        <TabsContent value="fleet" className="mt-4 space-y-4">
          <div className="grid gap-4 lg:grid-cols-2">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium">
                  Fuel Consumption by Vehicle Type
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={fuelByVehicleType}>
                    <CartesianGrid
                      strokeDasharray="3 3"
                      stroke="#e2e8f0"
                      vertical={false}
                    />
                    <XAxis
                      dataKey="type"
                      tick={{ fontSize: 10 }}
                      stroke="#94a3b8"
                      axisLine={false}
                      tickLine={false}
                    />
                    <YAxis
                      tick={{ fontSize: 11 }}
                      stroke="#94a3b8"
                      axisLine={false}
                      tickLine={false}
                    />
                    <Tooltip contentStyle={tooltipStyle} />
                    <Bar
                      dataKey="consumption"
                      name="L/100km"
                      fill={colors.chart1}
                      radius={[2, 2, 0, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium">
                  Fleet Cost Distribution
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={fuelByVehicleType}>
                    <CartesianGrid
                      strokeDasharray="3 3"
                      stroke="#e2e8f0"
                      vertical={false}
                    />
                    <XAxis
                      dataKey="type"
                      tick={{ fontSize: 10 }}
                      stroke="#94a3b8"
                      axisLine={false}
                      tickLine={false}
                    />
                    <YAxis
                      tick={{ fontSize: 11 }}
                      stroke="#94a3b8"
                      axisLine={false}
                      tickLine={false}
                      tickFormatter={(v) => `€${v / 1000}k`}
                    />
                    <Tooltip
                      contentStyle={tooltipStyle}
                      formatter={(value) => formatCurrency(value as number)}
                    />
                    <Bar
                      dataKey="cost"
                      name="Total Cost"
                      fill={colors.chart2}
                      radius={[2, 2, 0, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </div>

          {/* Vehicle Status Table */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">
                Vehicle Status Overview
              </CardTitle>
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <Skeleton className="h-48 w-full" />
              ) : vehicles && vehicles.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="text-xs">Vehicle</TableHead>
                      <TableHead className="text-xs">Model</TableHead>
                      <TableHead className="text-right text-xs">Fuel</TableHead>
                      <TableHead className="text-right text-xs">
                        Mileage
                      </TableHead>
                      <TableHead className="text-right text-xs">
                        Status
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {vehicles.map((vehicle) => (
                      <TableRow key={vehicle.id}>
                        <TableCell className="flex items-center gap-2 text-sm font-medium">
                          <Truck className="text-muted-foreground h-3.5 w-3.5" />
                          {vehicle.licensePlate}
                        </TableCell>
                        <TableCell className="text-sm">
                          {vehicle.vehicleModel ?? ''}
                        </TableCell>
                        <TableCell className="text-right text-sm">
                          <span
                            className={
                              vehicle.fuel < 25
                                ? 'text-red-600'
                                : vehicle.fuel < 50
                                  ? 'text-amber-600'
                                  : 'text-green-600'
                            }
                          >
                            {vehicle.fuel}%
                          </span>
                        </TableCell>
                        <TableCell className="text-right text-sm">
                          {(vehicle.mileage / 1000).toFixed(1)}k km
                        </TableCell>
                        <TableCell className="text-right">
                          <VehicleStatusBadge status={vehicle.status} />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <p className="text-muted-foreground py-8 text-center text-sm">
                  No vehicles found
                </p>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  )
}
